import Header from "~/components/ui/SectionHeader"
import Image from "~/components/ui/Image"
import { type SectionProps as SectionProps } from "~/types/deco"
export interface layout {
    /**
     * @title Alinhamento do cabeçalho
     */
    headerAlignment?: 'center' | 'left'
    /**
     * @title Número de posts
     * @description Default is 12
     * @default 12
     */
    numberOfPosts?: number
    /**
     * @title Posts por linha
     * @description Up to 6. Default is 4
     * @default 4
     */
    postsPerLine?: number
}
export interface Data {
    id: string
    permalink: string
    media_type: string
    media_url: string
}
export interface Props {
    /**
     * @title Título
     */
    title?: string
    /**
     * @title Descrição
     */
    description?: string
    /**
     * @description Obtenha o token no aplicativo do Facebook. Expira a cada 90 dias.
     * @format textarea
     */
    facebookToken: string
    layout?: layout
}
/**
 * SWR de 5min sobre o resultado do loader (profile `static`, resolvido em
 * @decocms/blocks/sdk/cacheHeaders → loader.fresh = 300_000).
 *
 * Sem isto o loader bate no Instagram Graph API a CADA render da section — um
 * fetch externo bloqueante no SSR de toda página que a contém.
 *
 * Seguro aqui porque a chave do cache de section é `component + hash(props
 * CRUAS)` (sectionLoaders.ts:126): sem UA, sem cookie, sem URL. Este loader não
 * lê nada do request (`_req` é ignorado) e o registro em setup/section-loaders.ts
 * não aplica mixin de device — logo não há o vazamento mobile↔desktop que
 * impede cachear as outras sections. As props (`facebookToken`, `layout`,
 * textos) são pequenas e vêm só do CMS, então a chave é estável e a entrada
 * é barata.
 *
 * O valor precisa ser um literal numa linha só: o codegen faz regex no
 * texto-fonte (@decocms/blocks-cli/scripts/generate-sections.ts:61).
 */
export const cache = "static"

export async function loader({ title, description, facebookToken, layout }: Props, _req: Request) {
    const fields = ['media_url', 'media_type', 'permalink']
    const joinFields = fields.join(',')
    const url =
        `https://graph.instagram.com/me/media?access_token=${facebookToken}&fields=${joinFields}`
    const { data } = (await fetch(url)
        .then((r) => r.json())
        .catch((err) => {
            console.error('error fetching posts from instagram', err)
            return { data: [] }
        })) as {
            data: Data[]
        }
    return {
        data: data?.slice(0, layout?.numberOfPosts ?? 12),
        title,
        description,
        layout,
    }
}
export default function InstagramPosts({
    title,
    description,
    layout,
    data = [
        {
            id: 'placeholderInsta',
            permalink: '#',
            media_type: 'IMAGE',
            media_url: '',
        },
    ],
}: SectionProps<typeof loader>) {
    return (
        <div className='w-full container px-4 py-8 flex flex-col gap-14 lg:gap-20 lg:py-10 lg:px-0'>
            <Header
                title={title}
                description={description}
                alignment={layout?.headerAlignment || 'center'}
            />
            <div className='hidden lg:grid-cols-1 lg:grid-cols-2 lg:grid-cols-3 lg:grid-cols-4 lg:grid-cols-5 lg:grid-cols-6'>
            </div>
            <div
                className={`grid grid-cols-2 lg:grid-cols-${
                    layout?.postsPerLine || 4
                } gap-4 items-center justify-center place-items-center`}
            >
                {data.map((item) => (
                    <a
                        key={item.id}
                        href={item.permalink}
                        target='_blank'
                        title='Visite nosso instagram'
                        className='rounded-lg overflow-hidden w-full max-w-[350px] sm:max-w-[350px] group'
                        rel='noreferrer'
                    >
                        {item.media_type === 'IMAGE'
                            ? (
                                <Image
                                    className='max-w-full max-h-full object-cover w-full group-hover:scale-110  transition duration-400 group-hover:brightness-90'
                                    src={item.media_url ?? ''}
                                    alt='Imagem do instagram'
                                    width={350}
                                    height={350}
                                    loading='lazy'
                                />
                            )
                            : (
                                <video controls className='max-w-full max-h-full object-cover'>
                                    <source src={item.media_url}></source>
                                </video>
                            )}
                    </a>
                ))}
            </div>
        </div>
    )
}
export { default as LoadingFallback } from "~/components/LoadingFallback"
